import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { HpRespCollectionsModuleItemOld } from './HpRespCollectionsModuleItemOld';
import { HpRespModuleContainerOld } from '../HpRespModuleContainerOld/HpRespModuleContainerOld';
import { RenderIn, device } from '../../utils/RenderIn';

import styles from './HpRespCollectionsModuleOld.scss';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import {
    trackCollectionsImpression,
    trackCollectionsItemClick,
    trackArrowClick,
} from './helpers/CollectionsTrackingOld';
import { slugify } from '../../utils/slugify';

function HpRespCollectionsModuleOldComponent({ componentModule, useLazyLoadImages }) {
    if (!componentModule) {
        return null;
    }

    const { title, items } = componentModule;
    const dataTn = `${slugify(title)}-container`;

    return (
        <HpRespModuleContainerOld dataTn={dataTn} addTopGap={!title}>
            <HpRespModuleContainerOld.SubHeader>{title}</HpRespModuleContainerOld.SubHeader>
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {() => {
                    const itemsPerPage = 5;

                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handleIndexChange }) => (
                                <Carousel
                                    slideCarouselItems
                                    classNames={{ wrapper: styles.wrapper }}
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index, isVisible }) => (
                                        <HpRespCollectionsModuleItemOld
                                            item={items[index]}
                                            isVisible={isVisible}
                                            useLazyLoadImages={useLazyLoadImages}
                                            onClick={() =>
                                                trackCollectionsItemClick({
                                                    item: items[index],
                                                    index,
                                                })
                                            }
                                        />
                                    )}
                                    onIndexChange={handleIndexChange}
                                    onLeftArrowClick={() => trackArrowClick({ type: 'previous' })}
                                    onRightArrowClick={() => trackArrowClick({ type: 'next' })}
                                />
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 3.5;
                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handlePageChange }) => (
                                <Swiper
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={(currentPage, previousPage) => {
                                        trackArrowClick({
                                            type: previousPage > currentPage ? 'previous' : 'next',
                                        });
                                        handlePageChange(currentPage);
                                    }}
                                >
                                    {items.map((item, index) => (
                                        <HpRespCollectionsModuleItemOld
                                            key={item.cmsId}
                                            item={item}
                                            useLazyLoadImages={useLazyLoadImages}
                                            onClick={() =>
                                                trackCollectionsItemClick({ item, index })
                                            }
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpRespModuleContainerOld>
    );
}

HpRespCollectionsModuleOldComponent.propTypes = {
    componentModule: PropTypes.object.isRequired,
    useLazyLoadImages: PropTypes.bool,
};

export const HpRespCollectionsModuleOld = createFragmentContainer(
    HpRespCollectionsModuleOldComponent,
    {
        componentModule: graphql`
            fragment HpRespCollectionsModuleOld_componentModule on CollectionsModule {
                title
                items {
                    cmsId
                    title
                    imageUrl
                    linkData {
                        ...SeoLink_linkData
                    }
                }
            }
        `,
    }
);
